<template>
  <div class="latest-warnings">
    <s-modal
      v-model="showModalHandler"
      size="xl"
      hide-footer
      @cancel="closeModal"
      centered
    >
      <template #modal-title>
        <div class="d-flex justify-content-between p-2">
          {{ $t('notification.latest-warnings') }}
          <b-button
            class="latest-warnings__button"
            size="sm"
            variant="outline-secondary"
            id="tooltip-subtitle"
          >
            <span class="material-icons-round">bookmark_border</span>
            {{ $t('notification.subtitle') }}
          </b-button>
          <b-tooltip
            target="tooltip-subtitle"
            placement="right"
          >
            <h2 class="h2 text-left my-3">{{ $t('notification.subtitle') }}</h2>
            <b-table striped hover :fields="subtitleFields" :items="subtitleItems">
              <template #cell(icon)="data">
                <span v-html="data.value"></span>
              </template>
              <template #cell(description)="data">
                <b v-html="data.item.title"></b> -
                <em v-html="data.item.purpose"></em>:
                <span v-html="data.value"></span>
              </template>
            </b-table>
          </b-tooltip>
        </div>
      </template>
      <template>
        <b-container fluid class="latest-warnings__cards d-flex justify-content-center h-100">
          <div :class="layoutGrid" class="d-flex mb-4 w-100">
            <div
              v-if="listNotifications.length > 0"
              class="card-latest-warning border mr-4 w-50 h-100 flex-fill"
            >
              <b-table
                show-empty
                :fields="fields"
                :items="listNotifications"
                style="position:relative; overflow-y:auto;"
                sticky-header
                class="h-100 no-align"
              >
                <template #cell(warning)="data">
                  <b-link
                    v-html="data.value"
                    :class="data.item.indicativeClass"
                    @click="openViewNotification(data.item.id, data.item.dateHourViewed)"
                  />
                </template>
                <template #cell(date_hour)="data">
                  <b-link
                    v-html="data.value"
                    :id="data.item.id"
                    :class="data.item.indicativeClass"
                    @click="openViewNotification(data.item.id, data.item.dateHourViewed)"
                  />
                  <b-tooltip :target="data.item.id" triggers="hover">
                    <span>
                      {{
                        $t('notification.grid-create-on', {
                          date: getFormattedDateTime(data.item.dateHourCreate, 'teste'),
                        })
                      }}
                    </span>
                  </b-tooltip>
                </template>
                <template #cell(title)="data">
                  <b-link
                    v-html="data.value"
                    :class="data.item.indicativeClass"
                    @click="openViewNotification(data.item.id, data.item.dateHourViewed)"
                  />
                </template>
              </b-table>
            </div>
            <s-empty-list
              v-else
              class="text-center"
              :empty-message="$t('messages.MA076.message')"
            />
            <div v-if="detailsNotification" class="border w-50 h-100 flex-fill">
              <b-card
                header-tag="header"
                footer-tag="footer"
                class="card-latest-warning h-100"
              >
                <template #header>
                  <h3 class="h3 mb-0 pt-2">
                    <span class="pt-2" v-html="detailsNotification.icon"></span>
                    <b>{{ detailsNotification.title }}</b>
                  </h3>
                </template>
                <b-card-body class="h-100" style="overflow-y:auto;">
                  <b-card-text style="max-height: 450px;">
                    <span v-html="detailsNotification.description"></span>
                  </b-card-text>
                </b-card-body>
                <template #footer>
                  <em class="d-flex justify-content-between">
                    <div>
                      <div id="tooltip-created">
                        <i class="far fa-calendar" />
                        {{ getFormattedDateTime(detailsNotification.dateHourCreate, 'date') }}
                      </div>
                      <b-tooltip target="tooltip-created" triggers="hover">
                        <span>
                          {{
                            $t('notification.grid-create-on', {
                              date: getFormattedDateTime(
                                detailsNotification.dateHourCreate,
                                'all'
                              )
                            })
                          }}
                        </span>
                      </b-tooltip>
                    </div>
                    <div v-if="detailsNotification.dateHourExpire">
                      <div id="tooltip-expired">
                        <i class="far fa-clock" />
                        {{ getFormattedDateTime(detailsNotification.dateHourExpire, 'date') }}
                      </div>
                      <b-tooltip target="tooltip-expired" triggers="hover">
                        <span>
                          {{
                            $t('notification.grid-expires-on', {
                              date: getFormattedDateTime(
                                detailsNotification.dateHourExpire,
                                'all'
                              ),
                            })
                          }}
                        </span>
                      </b-tooltip>
                    </div>
                    <div>
                      <div id="tooltip-viewed" v-if="detailsNotification.dateHourViewed">
                        <i class="fas fa-check-double text-primary"></i>
                        {{ getFormattedDateTime(detailsNotification.dateHourViewed, 'date') }}
                      </div>
                      <b-tooltip target="tooltip-viewed" triggers="hover">
                        <span>
                          {{
                            $t('notification.grid-read-on', {
                              date: getFormattedDateTime(
                                detailsNotification.dateHourViewed,
                                'all'
                              ),
                            })
                          }}
                        </span>
                      </b-tooltip>
                    </div>
                  </em>
                </template>
              </b-card>
            </div>
          </div>
        </b-container>
      </template>
    </s-modal>
  </div>
</template>

<script>
import NotificationService from '@/domain/services/notification';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import LatestWarningSubtitleHeader from '@/domain/data/generalPanel/latestWarning/latest-warning-subtitle-header.json';
import LatestWarningSubtitleItems from '@/domain/data/generalPanel/latestWarning/latest-warning-subtitle-items.json';

export default {
  name: 'LatestWarningModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    showModal: {
      type: Boolean,
    },
  },
  data: () => ({
    listNotifications: [],
    layoutGrid: 'justify-content-center',
    detailsNotification: '',
    icons: {
      success: '<i class="fas fa-check" />',
      maintenance: '<i class="fas fa-tools" />',
      newcontent: '<i class="fas fa-sync-alt" />',
      default: '<i class="fas fa-bullhorn" />',
    },
  }),
  computed: {
    dateLabelFormat() {
      return `D ${this.$t('general.at')} TT`;
    },
    showModalHandler: {
      get() {
        if (this.showModal && !this.detailsNotification && this.listNotifications.length > 0) {
          this.openViewNotification(this.listNotifications[0].id, this.listNotifications[0].viewed);
        }
        return this.showModal;
      },
      set(val) {
        if (!val) {
          this.closeModal();
        }
      },
    },
    subtitleFields() {
      return LatestWarningSubtitleHeader.map(item => ({
        key: item.key,
        label: this.$t(item.label),
      }));
    },
    subtitleItems() {
      return LatestWarningSubtitleItems.map(item => ({
        icon: item.icon,
        title: this.$t(item.title),
        purpose: this.$t(item.purpose),
        description: this.$t(item.description),
      }));
    },
    fields() {
      return [
        { key: 'warning', label: this.$t('notification.notice'), thStyle: 'background-color: var(--color-table-header-bg)' },
        { key: 'date_hour', label: this.$t('notification.date-hour'), thStyle: 'background-color: var(--color-table-header-bg)' },
        { key: 'title', label: this.$t('notification.title'), thStyle: 'background-color: var(--color-table-header-bg)' },
      ];
    },

  },
  created() {
    this.getListNotification();
  },
  methods: {
    async getListNotification() {
      await NotificationService.getListNotification()
        .then((notifications) => {
          this.listNotifications = notifications.map((notification) => ({
              ...notification,
              warning: this.getIconNotification(notification.typeNotification),
              date_hour: this.getFormattedDateTime(notification.dateHourCreate, 'date'),
              indicativeClass: !notification.dateHourViewed ? 'new-notification' : '',
              _cellVariants: notification.id === this.detailsNotification.id ? { warning: 'current', date_hour: 'current', title: 'current' } : '',
          }));
        });

      if (this.listNotifications.length > 0) {
        this.layoutGrid = 'justify-content-between';
      }
    },
    async updateDateLatestViewed(idNotificationViewed) {
      await NotificationService.updateLatestVisualization(idNotificationViewed);
      this.getListNotification();
    },
    openViewNotification(idNotificationViewed, viewed) {
      if (!viewed) {
        this.updateDateLatestViewed(idNotificationViewed);
      }

      const today = new Date(Date.now()).toISOString();

      const notificationSelected = this.listNotifications
        .filter(n => (n.id === idNotificationViewed));
        this.detailsNotification = {
          id: notificationSelected[0].id,
          icon: notificationSelected[0].warning,
          title: notificationSelected[0].title,
          description: notificationSelected[0].description,
          dateHourCreate: notificationSelected[0].dateHourCreate,
          dateHourExpire: notificationSelected[0].dateHourExpire,
          dateHourViewed: notificationSelected[0].dateHourViewed || today,
        };

        this.getListNotification();
    },
    getIconNotification(iconSelected) {
      const icon = Object.keys(this.icons).find((key) => iconSelected === key) || 'default';
      return this.icons[icon];
    },
    getFormattedDateTime(date, type) {
      const typeFormatDateTime = {
        date: formats.DATE_SHORT,
        hour: formats.TIME_24_SIMPLE,
        default: formats.DATETIME_SHORT,
      };

      const findTypeOfDate = Object.keys(typeFormatDateTime).find((key) => type === key) || 'default';
      const keyTypeDate = typeFormatDateTime[findTypeOfDate];

      return dateTimeFormatter(date, keyTypeDate);
    },
    closeModal() {
      this.$emit('close-modal');
    },
  },
};
</script>
<style lang="sass" scoped>
::v-deep
  * > table.table tr
    border: 1px #CCC solid

  * > table.table th,
  * > table.table td:nth-child(-n+2)
    text-align: center

table.table thead th, .card-header, .card-footer,
::v-deep * > table.table td.table-current
  background-color: var(--color-table-header-bg) !important

.b-table-sticky-header
  max-height: 500px !important
  margin-bottom: unset

.new-notification
  font-weight: bolder
  font-size: 1.1rem

.latest-warnings
  &__button
    & span.material-icons-round
      font-size: 1.2rem
      vertical-align: bottom

  &__cards
    gap: 20px

::v-deep .modal-header
  display: block

.card-latest-warning
  background-color: var(--color-background-regular)
  height: 500px !important

.tooltip
  background-color: var(--color-background-regular)

@page
  margin: 0 auto

  @media print
    .modal-dialog,
    .modal-content
      max-width: 100vw
      width: 100vw
      height: 100vh
      margin: 0 auto

    .modal-content
      position: absolute
      top: 0
      left: 0
      right: 0
      bottom: 0
</style>
